import React from 'react';
import {
  Target,
  ChartBar,
  Shield,
  ArrowRight,
  Users,
  Building2,
  GitBranch,
  AlertTriangle,
  CheckCircle,
  Workflow,
  Database,
  FileText,
  Calendar
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const CEMACFinanceAnalysis = () => {
  const projectOverview = {
    title: "Réglementation Finance Digitale CEMAC",
    phase: "Mise en œuvre",
    timeline: "2024-2025",
    status: "En déploiement"
  };

  const regulatoryFramework = {
    mainRegulations: [
      {
        title: "Règlement COBAC R-2024/02",
        scope: "Services Financiers Digitaux",
        status: "En vigueur",
        elements: [
          "Agrément des établissements digitaux",
          "Gestion des risques IT",
          "Protection des données",
          "Reporting réglementaire"
        ]
      },
      {
        title: "Instruction BEAC",
        scope: "Monnaie Électronique",
        status: "En application",
        elements: [
          "Émission monnaie électronique",
          "Interopérabilité services",
          "Sécurité transactions",
          "Supervision prudentielle"
        ]
      }
    ]
  };

  const technicalRequirements = {
    infrastructure: [
      {
        name: "Cloud & Hébergement",
        requirements: [
          "Infrastructure cloud conforme",
          "Localisation données CEMAC",
          "Redondance géographique",
          "Plan PRA/PCA"
        ]
      },
      {
        name: "Sécurité & Contrôle",
        requirements: [
          "Authentification forte",
          "Chiffrement données",
          "Audit logs centralisés",
          "Surveillance temps réel"
        ]
      },
      {
        name: "Reporting & Analytics",
        requirements: [
          "Reporting automatisé",
          "Tableaux de bord temps réel",
          "Analytics risques",
          "KPIs réglementaires"
        ]
      }
    ],
    standards: [
      {
        name: "Normes Techniques",
        specifications: [
          "ISO 27001",
          "PCI DSS",
          "NIST Framework",
          "ISO 20022"
        ]
      },
      {
        name: "Standards Données",
        specifications: [
          "Format reporting CEMAC",
          "Taxonomie XBRL",
          "APIs standards",
          "Modèles données"
        ]
      }
    ]
  };

  const implementationPlan = {
    phases: [
      {
        name: "Phase 1 - Conformité Base",
        period: "2024 T2-T3",
        status: "En cours",
        deliverables: [
          "Infrastructure conforme",
          "Contrôles basiques",
          "Reporting essentiel",
          "Formation initiale"
        ]
      },
      {
        name: "Phase 2 - Avancée",
        period: "2024 T4 - 2025 T1",
        status: "Planifié",
        deliverables: [
          "Automatisation complète",
          "Analytics avancés",
          "Tableaux de bord",
          "Contrôles étendus"
        ]
      }
    ],
    governance: {
      committees: [
        "Comité conformité",
        "Comité risques IT",
        "Comité audit",
        "Groupe technique"
      ],
      metrics: [
        "Taux conformité",
        "Incidents sécurité",
        "Qualité données",
        "Délais reporting"
      ]
    }
  };

  const impacts = {
    institutions: [
      {
        type: "Établissements Financiers",
        effects: [
          "Investissements IT majeurs",
          "Réorganisation process",
          "Formation personnel",
          "Nouveaux contrôles"
        ]
      },
      {
        type: "Fintechs",
        effects: [
          "Barrière entrée accrue",
          "Coûts conformité",
          "Standards stricts",
          "Supervision renforcée"
        ]
      }
    ],
    metrics: [
      {
        indicator: "Coûts Conformité",
        value: "+25-35%",
        baseline: "Budget IT"
      },
      {
        indicator: "Délai Mise en Conformité",
        value: "12-18 mois",
        baseline: "Moyenne"
      },
      {
        indicator: "Personnel Conformité",
        value: "+40%",
        baseline: "ETP"
      }
    ]
  };

  const challenges = [
    {
      category: "Technique",
      items: [
        {
          challenge: "Infrastructure Cloud",
          impact: "Élevé",
          mitigation: "Partenariats stratégiques"
        },
        {
          challenge: "Automatisation Reporting",
          impact: "Élevé",
          mitigation: "Outils spécialisés"
        }
      ]
    },
    {
      category: "Opérationnel",
      items: [
        {
          challenge: "Compétences IT",
          impact: "Élevé",
          mitigation: "Formation intensive"
        },
        {
          challenge: "Délais Conformité",
          impact: "Moyen",
          mitigation: "Plan progressif"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {projectOverview.title}
          </h1>
          <div className="flex flex-wrap gap-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Phase: {projectOverview.phase}
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              Timeline: {projectOverview.timeline}
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              Status: {projectOverview.status}
            </span>
          </div>
        </div>

        {/* Cadre Réglementaire */}
        <Section title="Cadre Réglementaire" icon={Shield}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {regulatoryFramework.mainRegulations.map((reg, idx) => (
              <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-medium">{reg.title}</h3>
                    <p className="text-sm text-gray-600">{reg.scope}</p>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                    {reg.status}
                  </span>
                </div>
                <ul className="space-y-2">
                  {reg.elements.map((element, i) => (
                    <li key={i} className="flex items-center text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      {element}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* Exigences Techniques */}
        <Section title="Exigences Techniques" icon={GitBranch}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {technicalRequirements.infrastructure.map((infra, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{infra.name}</h3>
                  <ul className="space-y-2">
                    {infra.requirements.map((req, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <Database className="w-4 h-4 mr-2 text-blue-500" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalRequirements.standards.map((standard, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{standard.name}</h3>
                  <div className="flex flex-wrap gap-2">
                    {standard.specifications.map((spec, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Plan Implementation */}
        <Section title="Plan de Mise en Conformité" icon={Workflow}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {implementationPlan.phases.map((phase, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{phase.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      phase.status === 'En cours'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {phase.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Période: {phase.period}</p>
                  <ul className="space-y-2">
                    {phase.deliverables.map((deliverable, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                        {deliverable}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-4">Gouvernance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-2">Comités</h4>
                  <ul className="space-y-1">
                    {implementationPlan.governance.committees.map((committee, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <Users className="w-4 h-4 mr-2 text-blue-500" />
                        {committee}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Métriques</h4>
                  <ul className="space-y-1">
                    {implementationPlan.governance.metrics.map((metric, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <ChartBar className="w-4 h-4 mr-2 text-green-500" />
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Impact */}
        <Section title="Impact sur le Secteur" icon={ChartBar}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impacts.metrics.map((metric, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">{metric.indicator}</div>
                  <div className="text-2xl font-semibold">{metric.value}</div>
                  <div className="text-sm text-gray-500 mt-1">{metric.baseline}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {impacts.institutions.map((inst, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{inst.type}</h3>
                  <ul className="space-y-2">
                    {inst.effects.map((effect, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                        {effect}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Défis */}
        <Section title="Défis et Risques" icon={AlertTriangle}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {challenges.map((category, idx) => (

              <div key={idx}>
                <h3 className="font-medium mb-4">{category.category}</h3>
                <div className="space-y-4">
                  {category.items.map((item, i) => (
                    <div key={i} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{item.challenge}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.impact === 'Élevé'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          Impact: {item.impact}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Mitigation: {item.mitigation}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

      </div>
    </div>
  );
};

export default CEMACFinanceAnalysis;